import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { API_URL } from './config/apiConfig';
import OrderDesign from './OrderDesign';
import { OrderCardBubbles } from './components/OrderCardBubbles';
import { playSoundEffect } from './hooks/useSoundEffects';
import { getEffectiveSoundOutputVolume } from './hooks/soundEffectsManager';
import './WarEffortSkeletonCrewSelection.css';

const SKELETON_CREW_SIZE = 30;
const PICK_TIME_MS = 10000;
const RARITY_PROBS = { common: 0.5, rare: 0.3, epic: 0.12, legendary: 0.08 };
const RARITIES = ['common', 'rare', 'epic', 'legendary'];

/** With blue/dark overlay – used on "Continue with your skeleton crew" page */
export const SKELETON_CREW_BG_STYLE = {
  background: `linear-gradient(to bottom, rgba(2, 10, 16, 0.85) 0%, rgba(2, 10, 16, 0.9) 100%), url(${process.env.PUBLIC_URL || ''}/images/skeleton_crew.jpg) center center / cover no-repeat`
};

/** Image only, no filter – used on skeleton crew selection page */
const SKELETON_CREW_BG_STYLE_NO_FILTER = {
  background: `url(${process.env.PUBLIC_URL || ''}/images/skeleton_crew.jpg) center center / cover no-repeat`
};

function pickByRarity(ordersByRarity) {
  const r = Math.random();
  let acc = 0;
  for (const rarity of RARITIES) {
    acc += RARITY_PROBS[rarity];
    if (r < acc) {
      const pool = ordersByRarity[rarity];
      if (pool && pool.length > 0) return pool[Math.floor(Math.random() * pool.length)];
      break;
    }
  }
  const fallback = RARITIES.find(r => ordersByRarity[r]?.length > 0);
  const pool = fallback ? ordersByRarity[fallback] : [];
  return pool[Math.floor(Math.random() * pool.length)];
}

/** Build 30 random order IDs from the pool (respecting max 3 per order, 1 for unique). Returns null if pool can't fill 30. */
function buildRandomCrew(ordersByRarity) {
  const countInCrew = {};
  const result = [];
  for (let i = 0; i < SKELETON_CREW_SIZE; i++) {
    const canAdd = (o) => {
      const c = countInCrew[o.id] || 0;
      const max = o.is_unique === true ? 1 : 3;
      return c < max;
    };
    const available = [
      ...(ordersByRarity.common || []).filter(canAdd),
      ...(ordersByRarity.rare || []).filter(canAdd),
      ...(ordersByRarity.epic || []).filter(canAdd),
      ...(ordersByRarity.legendary || []).filter(canAdd)
    ];
    if (available.length === 0) return null;
    const order = available[Math.floor(Math.random() * available.length)];
    result.push(order.id);
    countInCrew[order.id] = (countInCrew[order.id] || 0) + 1;
  }
  return result;
}

function WarEffortSkeletonCrewSelection({ purpose, selectedFaction, onComplete, onBack, isSubmitting = false, submitError = null }) {
  const [orders, setOrders] = useState([]);
  const [ordersByRarity, setOrdersByRarity] = useState({ common: [], rare: [], epic: [], legendary: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [skeletonCrew, setSkeletonCrew] = useState([]);
  const [offeredOrders, setOfferedOrders] = useState([]);
  const [timeLeft, setTimeLeft] = useState(PICK_TIME_MS / 1000);
  const [animatingOrderId, setAnimatingOrderId] = useState(null);
  const [flyAnimation, setFlyAnimation] = useState(null);
  const timerRef = useRef(null);
  const pickedThisRoundRef = useRef(false);
  const timeRunningOutPlayedRef = useRef(false);
  const timeRunningOutAudioRef = useRef(null);
  const offerablePoolEmptyRef = useRef(false);
  const counterRef = useRef(null);
  const offerRefs = useRef({});

  const fetchOrders = useCallback(async () => {
    if (!selectedFaction) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const lang = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language.slice(0, 2).toLowerCase() : 'en';
      const res = await fetch(`${API_URL}/orders?faction=${encodeURIComponent(selectedFaction)}`, {
        headers: { Authorization: `Bearer ${token}`, 'Accept-Language': lang }
      });
      if (!res.ok) throw new Error('Failed to load orders');
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [selectedFaction]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (orders.length === 0) return;
    const byRarity = { common: [], rare: [], epic: [], legendary: [] };
    orders.forEach(o => {
      if ((o.availability || '').toLowerCase() === 'created') return;
      const r = (o.rarity || 'common').toString().toLowerCase();
      const key = RARITIES.includes(r) ? r : 'common';
      byRarity[key].push(o);
    });
    setOrdersByRarity(byRarity);
  }, [orders]);

  const offerableCount = useMemo(
    () => orders.filter(o => (o.availability || '').toLowerCase() !== 'created').length,
    [orders]
  );

  const handleRandomize = useCallback(() => {
    const randomCrew = buildRandomCrew(ordersByRarity);
    if (!randomCrew) return;
    stopTimeRunningOutSound();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setOfferedOrders([]);
    setSkeletonCrew(randomCrew);
    playSoundEffect('/sounds/click.mp3');
  }, [ordersByRarity]);

  /** Cost distribution for skeleton crew (0..12), same as Strategy.js cost bars */
  const skeletonCrewCostCounts = useMemo(() => {
    const counts = [...Array(13).keys()].map(() => 0);
    const orderById = new Map((orders || []).map(o => [o.id, o]));
    skeletonCrew.forEach(id => {
      const order = orderById.get(id);
      if (order != null && typeof order.cost === 'number' && order.cost >= 0 && order.cost <= 12) {
        counts[order.cost] += 1;
      }
    });
    return counts;
  }, [orders, skeletonCrew]);

  const offerNextThree = useCallback(() => {
    pickedThisRoundRef.current = false;
    const countInCrew = {};
    skeletonCrew.forEach(id => { countInCrew[id] = (countInCrew[id] || 0) + 1; });
    const canAdd = (order) => {
      const count = countInCrew[order.id] || 0;
      const max = order.is_unique === true ? 1 : 3;
      return count < max;
    };
    const filterByCap = (arr) => (arr || []).filter(canAdd);
    const byRarity = {
      common: filterByCap(ordersByRarity.common),
      rare: filterByCap(ordersByRarity.rare),
      epic: filterByCap(ordersByRarity.epic),
      legendary: filterByCap(ordersByRarity.legendary)
    };
    const totalOfferable = byRarity.common.length + byRarity.rare.length + byRarity.epic.length + byRarity.legendary.length;
    const totalInRarity = (ordersByRarity.common?.length || 0) + (ordersByRarity.rare?.length || 0) + (ordersByRarity.epic?.length || 0) + (ordersByRarity.legendary?.length || 0);
    if (totalOfferable === 0) {
      if (totalInRarity > 0) {
        offerablePoolEmptyRef.current = true;
      }
      setOfferedOrders([]);
      return;
    }
    offerablePoolEmptyRef.current = false;
    const three = [];
    const usedIds = new Set();
    for (let i = 0; i < 3; i++) {
      let order = pickByRarity(byRarity);
      let attempts = 0;
      while (order && usedIds.has(order.id) && attempts < 20) {
        order = pickByRarity(byRarity);
        attempts++;
      }
      if (!order) break;
      usedIds.add(order.id);
      three.push(order);
    }
    setOfferedOrders(three);
    setTimeLeft(PICK_TIME_MS / 1000);
    timeRunningOutPlayedRef.current = false;
    timeRunningOutAudioRef.current = null;
  }, [ordersByRarity, skeletonCrew]);

  useEffect(() => {
    if (loading || orders.length === 0 || skeletonCrew.length >= SKELETON_CREW_SIZE) return;
    if (offeredOrders.length === 0) {
      if (offerablePoolEmptyRef.current) return;
      offerNextThree();
      return;
    }
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 0.1) return 0;
        return Math.max(0, prev - 0.1);
      });
    }, 100);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [loading, orders.length, skeletonCrew.length, offeredOrders.length, offerNextThree]);

  useEffect(() => {
    if (timeLeft <= 0 || timeLeft > 3) return;
    if (!timeRunningOutPlayedRef.current) {
      timeRunningOutPlayedRef.current = true;
      if (typeof document !== 'undefined' && document.hidden) return;
      const v = getEffectiveSoundOutputVolume(1);
      if (v === null) return;
      try {
        const audio = new Audio('/sounds/time_running_out.mp3');
        audio.volume = v;
        timeRunningOutAudioRef.current = audio;
        audio.play().catch(() => {});
      } catch (_) {}
    }
  }, [timeLeft]);

  useEffect(() => {
    if (timeLeft > 0 || offeredOrders.length === 0 || pickedThisRoundRef.current) return;
    pickedThisRoundRef.current = true;
    stopTimeRunningOutSound();
    playSoundEffect('/sounds/mulligan_enable.mp3');
    const randomIndex = Math.floor(Math.random() * offeredOrders.length);
    const chosen = offeredOrders[randomIndex];
    if (chosen) {
      const cardEl = offerRefs.current[chosen.id];
      const counterEl = counterRef.current;
      if (cardEl && counterEl) {
        const rect = cardEl.getBoundingClientRect();
        const counterRect = counterEl.getBoundingClientRect();
        const toX = counterRect.left + counterRect.width / 2;
        const toY = counterRect.top + counterRect.height / 2;
        setFlyAnimation({
          from: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
          to: { x: toX, y: toY },
          order: chosen
        });
      }
      setAnimatingOrderId(chosen.id);
      setSkeletonCrew(prev => [...prev, chosen.id]);
      setTimeout(() => {
        setFlyAnimation(null);
        setAnimatingOrderId(null);
        setOfferedOrders([]);
      }, 600);
    }
  }, [timeLeft, offeredOrders]);

  const stopTimeRunningOutSound = () => {
    if (timeRunningOutAudioRef.current) {
      try {
        timeRunningOutAudioRef.current.pause();
        timeRunningOutAudioRef.current.currentTime = 0;
      } catch (_) {}
      timeRunningOutAudioRef.current = null;
    }
  };

  const handlePickOrder = (order, event) => {
    if (skeletonCrew.length >= SKELETON_CREW_SIZE) return;
    stopTimeRunningOutSound();
    playSoundEffect('/sounds/mulligan_enable.mp3');
    pickedThisRoundRef.current = true;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setTimeLeft(0);
    const cardEl = event?.currentTarget;
    const counterEl = counterRef.current;
    if (cardEl && counterEl) {
      const rect = cardEl.getBoundingClientRect();
      const counterRect = counterEl.getBoundingClientRect();
      const toX = counterRect.left + counterRect.width / 2;
      const toY = counterRect.top + counterRect.height / 2;
      setFlyAnimation({
        from: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
        to: { x: toX, y: toY },
        order
      });
    }
    setAnimatingOrderId(order.id);
    setSkeletonCrew(prev => [...prev, order.id]);
    setTimeout(() => {
      setFlyAnimation(null);
      setAnimatingOrderId(null);
      setOfferedOrders([]);
    }, 600);
  };

  const viewportWrap = (content) => (
    <div className="skeleton-crew-viewport-wrapper">
      <div className="skeleton-crew-fullscreen-background" style={SKELETON_CREW_BG_STYLE_NO_FILTER} />
      <div className="skeleton-crew-viewport-content">
        {content}
      </div>
    </div>
  );

  if (loading) {
    return viewportWrap(
      <div className="skeleton-crew-selection">
        <div className="skeleton-crew-loading">Loading orders...</div>
      </div>
    );
  }
  if (error) {
    return viewportWrap(
      <div className="skeleton-crew-selection">
        <div className="skeleton-crew-error">{error}</div>
      </div>
    );
  }
  if (orders.length === 0 || offerableCount === 0) {
    return viewportWrap(
      <div className="skeleton-crew-selection">
        <div className="skeleton-crew-error">
          {orders.length === 0 ? 'No orders available for this faction.' : 'No offerable orders for this faction (excluding Created-only).'}
        </div>
      </div>
    );
  }

  if (skeletonCrew.length === SKELETON_CREW_SIZE) {
    const maxCount = Math.max(...skeletonCrewCostCounts, 1);
    const maxBarHeight = 60;
    return viewportWrap(
      <div className="skeleton-crew-selection skeleton-crew-complete-wrap">
        <div className="skeleton-crew-complete-message">
          <h3>Skeleton crew complete</h3>
          <p>You have selected 30 orders. Start the war effort mission with this crew.</p>
        </div>
        <div className="skeleton-crew-cost-graph-wrapper">
          <div className="skeleton-crew-cost-bar-container">
            {[...Array(13).keys()].map(cost => {
              const count = skeletonCrewCostCounts[cost];
              const barHeight = maxCount > 0 ? (count / maxCount) * maxBarHeight : 0;
              return (
                <div key={cost} className="skeleton-crew-cost-bar-segment">
                  <div
                    className="skeleton-crew-cost-bar-fill"
                    style={{ height: `${barHeight}px` }}
                    title={`Cost ${cost}: ${count} orders`}
                  />
                  <span className="skeleton-crew-cost-bar-label">{cost}</span>
                </div>
              );
            })}
          </div>
        </div>
        {submitError && (
          <div className="skeleton-crew-error" role="alert">
            {submitError}
          </div>
        )}
        <button
          type="button"
          className="skeleton-crew-start-mission-button"
          onClick={() => {
            if (isSubmitting) return;
            playSoundEffect('/sounds/click.mp3');
            onComplete(skeletonCrew);
          }}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Starting…' : 'Start Mission'}
        </button>
      </div>
    );
  }

  const timerPercent = (timeLeft / (PICK_TIME_MS / 1000)) * 100;
  const isPulse = timeLeft > 0 && timeLeft <= 5;

  return viewportWrap(
    <div className="skeleton-crew-selection">
      <div className="skeleton-crew-header">
        <h3>Select Skeleton Crew</h3>
        <div
          ref={counterRef}
          className="skeleton-crew-counter"
          aria-live="polite"
        >
          <span className="skeleton-crew-count-num">{skeletonCrew.length}</span>
          <span className="skeleton-crew-count-sep">/</span>
          <span className="skeleton-crew-count-total">{SKELETON_CREW_SIZE}</span>
        </div>
      </div>

      <div className={`skeleton-crew-timer-wrap ${isPulse ? 'skeleton-crew-timer-pulse' : ''}`}>
        <div
          className="skeleton-crew-timer-circle"
          style={{ '--timer-percent': timerPercent }}
        />
        <span className="skeleton-crew-timer-text">{Math.ceil(timeLeft)}s</span>
      </div>

      <p className="skeleton-crew-instruction">
        Choose one order to add to your skeleton crew. You have 10 seconds per pick.
      </p>

      <div className="skeleton-crew-randomize-wrap">
        <button
          type="button"
          className="skeleton-crew-randomize-button"
          onClick={handleRandomize}
          aria-label="Fill skeleton crew with 30 random orders instantly"
        >
          Randomize
        </button>
      </div>

      <div className="skeleton-crew-offers">
        {offeredOrders.map(order => {
          const rarity = (order.rarity || 'common').toLowerCase();
          return (
            <div
              key={order.id}
              ref={el => { if (el) offerRefs.current[order.id] = el; }}
              className={`skeleton-crew-offer-card ${animatingOrderId === order.id ? 'skeleton-crew-card-animating' : ''}`}
              onClick={(e) => animatingOrderId == null && timeLeft > 0 && handlePickOrder(order, e)}
              role="button"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePickOrder(order, e); } }}
              aria-label={`Select ${order.name}`}
            >
              <div
                className={`reward-order-container rarity-${rarity}`}
                style={{ width: '390px', height: '390px', position: 'relative', margin: 0, padding: 0 }}
              >
                <OrderDesign
                  order={order}
                  count={1}
                  scale={1.56}
                  rarity={order.rarity}
                  noMargin={true}
                  gameState={null}
                />
                <OrderCardBubbles rarity={order.rarity || 'common'} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="skeleton-crew-cost-graph-wrapper">
        <div className="skeleton-crew-cost-bar-container">
          {[...Array(13).keys()].map(cost => {
            const count = skeletonCrewCostCounts[cost];
            const maxCount = Math.max(...skeletonCrewCostCounts, 1);
            const maxBarHeight = 60;
            const barHeight = maxCount > 0 ? (count / maxCount) * maxBarHeight : 0;
            return (
              <div key={cost} className="skeleton-crew-cost-bar-segment">
                <div
                  className="skeleton-crew-cost-bar-fill"
                  style={{ height: `${barHeight}px` }}
                  title={`Cost ${cost}: ${count} orders`}
                />
                <span className="skeleton-crew-cost-bar-label">{cost}</span>
              </div>
            );
          })}
        </div>
      </div>

      {flyAnimation && (
        <div
          className="skeleton-crew-fly-clone"
          style={{
            '--fly-from-left': `${flyAnimation.from.left}px`,
            '--fly-from-top': `${flyAnimation.from.top}px`,
            '--fly-width': `${flyAnimation.from.width}px`,
            '--fly-height': `${flyAnimation.from.height}px`,
            '--fly-to-x': `${flyAnimation.to.x}px`,
            '--fly-to-y': `${flyAnimation.to.y}px`
          }}
        >
          <div
            className={`reward-order-container rarity-${(flyAnimation.order.rarity || 'common').toLowerCase()}`}
            style={{ width: '390px', height: '390px', position: 'relative', margin: 0, padding: 0 }}
          >
            <OrderDesign
              order={flyAnimation.order}
              count={1}
              scale={1.56}
              rarity={flyAnimation.order.rarity}
              noMargin={true}
              gameState={null}
            />
            <OrderCardBubbles rarity={flyAnimation.order.rarity || 'common'} />
          </div>
        </div>
      )}
    </div>
  );
}

export default WarEffortSkeletonCrewSelection;
